import { useState } from 'react';
import type { Page } from '../domain/common';
import { useLoad } from './useLoad';

/**
 * The first page of a paged list on mount (with the page states' loading,
 * error and `reload`), plus `loadMore` for the pages after it. A failed
 * `loadMore` keeps what's shown and reports through `moreError`.
 */
export function usePagedLoad<T>(loadPage: (page: number) => Promise<Page<T>>, key: string) {
  const first = useLoad(() => loadPage(1), key);
  const [more, setMore] = useState<T[]>([]);
  const [nextPage, setNextPage] = useState<number | null | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);

  const items = first.status === 'ready' ? [...first.data.items, ...more] : [];
  const next =
    first.status !== 'ready' ? null : nextPage === undefined ? first.data.nextPage : nextPage;

  const loadMore = async () => {
    if (next === null) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const page = await loadPage(next);
      setMore((prev) => [...prev, ...page.items]);
      setNextPage(page.nextPage);
    } catch (e) {
      setMoreError(e);
    } finally {
      setLoadingMore(false);
    }
  };

  return { first, items, hasMore: next !== null, loadMore, loadingMore, moreError };
}
